import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import BasicButton from "../../components/basics/BasicButton";
import { useGetPrintFilesQuery } from "./printFilesApi";
import type { PrintBatch, PrintFile } from "./printFilesApi";
import "./printFiles.css";

// /admin/print-files: every print-ready file for the print shop, by batch (the 2025 favourites,
// the card test, later the weekly batches), each with its README for the shop. The files sit in
// a private bucket; the links in the answer are signed for an hour and renewed before they run
// out. A first step toward the print tool (/admin/posters): its batches are meant to land here.

const RENEW_BEFORE_MS = 2 * 60 * 1000;

export function formatSize(bytes: number): string {
  if (bytes < 1000) return `${bytes} B`;
  if (bytes < 1e6) return `${Math.round(bytes / 1000)} KB`;
  return `${(bytes / 1e6).toFixed(1)} MB`;
}

function formatDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
}

function useWide(query = "(min-width: 1024px)"): boolean {
  const [wide, setWide] = useState(() => typeof window !== "undefined" && window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setWide(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [query]);
  return wide;
}

const FileCard: React.FC<{ file: PrintFile }> = ({ file }) => (
  <li className="pf-file">
    <div className="pf-thumb">
      {file.thumbUrl ? <img src={file.thumbUrl} alt="" loading="lazy" /> : <span>{file.name.split(".").pop()}</span>}
    </div>
    <div className="pf-file-body">
      <p className="pf-file-name">{file.name}</p>
      <p className="pf-file-about">{file.about}</p>
      <p className="pf-file-meta">
        {file.pages ? `${file.pages} ${file.pages === 1 ? "page" : "pages"} · ` : ""}
        {formatSize(file.size)}
      </p>
      <a className="pf-btn" href={file.url} download={file.name}>
        download
      </a>
    </div>
  </li>
);

const Readme: React.FC<{ readme: NonNullable<PrintBatch["readme"]>; wide: boolean }> = ({ readme, wide }) => {
  const body = (
    <>
      <pre className="pf-readme-text">{readme.text}</pre>
      <a className="pf-readme-link" href={readme.url} download={readme.name}>
        download {readme.name}
      </a>
    </>
  );
  if (wide) {
    return (
      <aside className="pf-readme" aria-label="notes for the print shop">
        <h3 className="pf-readme-title">for the print shop</h3>
        {body}
      </aside>
    );
  }
  return (
    <details className="pf-readme">
      <summary className="pf-readme-title">for the print shop ({readme.name})</summary>
      {body}
    </details>
  );
};

const BatchSection: React.FC<{ batch: PrintBatch; wide: boolean }> = ({ batch, wide }) => (
  <section className="pf-batch" id={`batch-${batch.id}`}>
    <header className="pf-batch-head">
      <h2 className="pf-batch-title">{batch.title}</h2>
      {batch.summary && <p className="pf-batch-summary">{batch.summary}</p>}
      <p className="pf-batch-meta">
        {batch.fileCount} files · {formatSize(batch.totalSize)} · made {formatDay(batch.createdAt)}
      </p>
      {batch.zip && (
        <a className="pf-btn pf-btn--all" href={batch.zip.url} download={batch.zip.name}>
          download all ({formatSize(batch.zip.size)} zip)
        </a>
      )}
    </header>
    <div className={`pf-batch-body ${batch.readme ? "has-readme" : ""}`}>
      {/* on a phone the README folds up right under the batch head; on a wide screen it sits beside the files */}
      {batch.readme && !wide && <Readme readme={batch.readme} wide={false} />}
      <div className="pf-groups">
        {batch.groups.map((group) => (
          <div key={group.title} className="pf-group">
            <h3 className="pf-group-title">{group.title}</h3>
            <ul className="pf-files">
              {group.files.map((file) => (
                <FileCard key={file.path} file={file} />
              ))}
            </ul>
          </div>
        ))}
      </div>
      {batch.readme && wide && <Readme readme={batch.readme} wide />}
    </div>
  </section>
);

const PrintFilesPage: React.FC = () => {
  const [refreshKey, setRefreshKey] = useState(0);
  const { data, isLoading, isFetching, isError, refetch } = useGetPrintFilesQuery(
    refreshKey ? { refresh: true, key: refreshKey } : undefined
  );
  const wide = useWide();

  useEffect(() => {
    document.title = "print files · admin";
  }, []);

  // ask for fresh links a little before these stop working
  useEffect(() => {
    if (!data) return;
    const wait = new Date(data.linksExpireAt).getTime() - Date.now() - RENEW_BEFORE_MS;
    const timer = window.setTimeout(() => refetch(), Math.max(wait, 5_000));
    return () => window.clearTimeout(timer);
  }, [data, refetch]);

  const batches = data?.batches ?? [];

  return (
    <div className="pf-page">
      <div className="pf-content">
        <Link to="/admin" className="pf-back">
          ← admin
        </Link>
        <div className="pf-head">
          <div>
            <h1 className="pf-files-title">print files</h1>
            <p className="pf-hint">
              Print-ready files for the print shop, by batch. Links last an hour and renew on their own.
            </p>
          </div>
          <BasicButton
            text={isFetching ? "loading..." : "refresh"}
            onClick={() => setRefreshKey((k) => k + 1)}
            disabled={isFetching}
          />
        </div>

        {batches.length > 1 && (
          <nav className="pf-chips" aria-label="batches">
            {batches.map((b) => (
              <a key={b.id} href={`#batch-${b.id}`} className="pf-chip">
                {b.title}
              </a>
            ))}
          </nav>
        )}

        {isLoading ? (
          <p className="pf-state">loading the print files...</p>
        ) : isError ? (
          <div className="pf-state">
            <p>Could not load the print files.</p>
            <BasicButton text="try again" onClick={() => refetch()} />
          </div>
        ) : batches.length === 0 ? (
          <p className="pf-state">
            No print files yet. A batch shows up here once it is published from _print/ with
            pipeline/publish/publish.py.
          </p>
        ) : (
          batches.map((batch) => <BatchSection key={batch.id} batch={batch} wide={wide} />)
        )}
      </div>
    </div>
  );
};

export default PrintFilesPage;
